// ChatGuided.js - Version corrigée avec gestion améliorée des questions et accumulation des notes

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  HelpCircle, Send, Star, CheckCircle, Trophy, RefreshCw, 
  MessageCircle, Target, Sparkles, Loader, MessageSquare,
  ChevronDown, ChevronUp, BarChart3, Clock, Check,
  AlertCircle, FileText, Eye, PenTool, BookOpen, Bot, X
} from 'lucide-react';
import { askGPT } from '../components/OpenAIService';

// Configuration simplifiée
const POINTS_CONFIG = {
  singleChoice: 15,
  multipleChoice: 20,
  customResponse: 25,
  synthesis: 50
};

const QUESTIONS_BEFORE_HELP = 3;

// Utilitaire pour extraire les questions JSON
const extractQuestionsFromResponse = (response) => {
  try {
    let rawResponse = response;
    if (typeof response === 'object' && response.reply) {
      rawResponse = response.reply;
    }
    
    if (Array.isArray(rawResponse)) return rawResponse;

    if (typeof rawResponse === 'string') {
      const cleanResponse = rawResponse
        .replace(/```json|```/g, '')
        .replace(/^[^[]*/, '')
        .replace(/[^\]]*$/, '')
        .trim();
      
      if (cleanResponse) {
        const parsed = JSON.parse(cleanResponse);
        if (Array.isArray(parsed)) return parsed;
      }
    }
    
    if (typeof rawResponse === 'object' && rawResponse.questions && Array.isArray(rawResponse.questions)) {
      return rawResponse.questions;
    }
    
    return null;
  } catch (error) {
    console.error('Erreur parsing questions:', error);
    return null;
  }
};

// Hook principal avec contexte enrichi pour les questions
export const useChatGuided = (props, chatCore) => {
  const {
    userObjective,
    currentStepId,
    savedSources,
    actionPlan,
    userProgress: initialProgress,
    updateStepNoteSection,
    getStepNotes,
    saveStepSynthesis,
    getStepSynthesis
  } = props;

  // États essentiels
  const [chatMode, setChatMode] = useState('conversation');
  const [userProgress, setUserProgress] = useState(initialProgress || {
    totalPoints: 0,
    completedActions: 0
  });
  
  const [questionsQueue, setQuestionsQueue] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [customResponse, setCustomResponse] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  
  // États feedback
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showAladinnHelp, setShowAladinnHelp] = useState(false);
  const [synthesisContent, setSynthesisContent] = useState('');
  
  // Mode conversation libre
  const [freeConversationInput, setFreeConversationInput] = useState('');

  // États pour notifications
  const [syncNotifications, setSyncNotifications] = useState([]);

  // États pour la synthèse d'affichage (pour usage externe)
  const [synthesisData, setSynthesisData] = useState(null);

  const [currentSessionQuestions, setCurrentSessionQuestions] = useState(0);

  // Données contextuelles
  const contextualData = useMemo(() => {
    const currentStep = actionPlan?.steps?.find(s => s.id === currentStepId);
    return {
      userObjective,
      currentStep,
      savedSources,
      actionPlan,
      hasContext: !!(userObjective || currentStep || (savedSources && savedSources.length > 0))
    };
  }, [userObjective, currentStepId, actionPlan, savedSources]);

  const isGuidedMode = chatMode === 'guided';
  const canGenerateQuestions = isGuidedMode && 
    questionsQueue.length === 0 && 
    !currentQuestion && 
    !isGeneratingQuestions;

  // Récupération de la synthèse existante
  useEffect(() => {
    if (currentStepId && getStepSynthesis) {
      const existingSynthesis = getStepSynthesis(currentStepId);
      if (existingSynthesis?.content) {
        setSynthesisContent(existingSynthesis.content);
      }
      
      // Récupérer l'historique des questions répondues
      const stepNotes = getStepNotes(currentStepId);
      if (stepNotes.guidedHistory) {
        try {
          const parsedHistory = JSON.parse(stepNotes.guidedHistory);
          setAnsweredQuestions(parsedHistory);
        } catch (error) {
          console.error('Erreur parsing historique guidé:', error);
        }
      }
    }
  }, [currentStepId, getStepSynthesis, getStepNotes]);

  // Mise à jour de la synthèse d'affichage pour usage externe
  useEffect(() => {
    if (currentStepId && getStepNotes) {
      const stepNotes = getStepNotes(currentStepId);
      
      // Analyser les sections pour créer la synthèse d'affichage
      const decisions = stepNotes.decisions || '';
      const questions = stepNotes.questions || '';
      const recommendations = stepNotes.ai_recommendations || '';

      const decisionCount = decisions.split('\n').filter(line => 
        line.trim() && (line.includes('[Utilisateur]') || line.includes('✅'))
      ).length;
      
      const questionCount = questions.split('\n').filter(line => 
        line.trim() && (line.includes('[Utilisateur]') || line.includes('❓'))
      ).length;
      
      const recommendationCount = recommendations.split('\n').filter(line => 
        line.trim() && line.includes('[Aladinn]')
      ).length;

      // Extraire les derniers éléments
      const getLastElement = (content, marker) => {
        if (!content) return null;
        const lines = content.split('\n').filter(line => line.trim());
        const lastIndex = lines.findLastIndex(line => line.includes(marker));
        if (lastIndex === -1) return null;
        return lines.slice(lastIndex, lastIndex + 2).join(' ')
          .replace(/[🤖👤✅❓📋]/g, '').replace(/\[.*?\]/g, '').trim();
      };

      setSynthesisData({
        stepId: currentStepId,
        counts: {
          decisions: decisionCount,
          questions: questionCount,
          recommendations: recommendationCount,
          total: decisionCount + questionCount + recommendationCount
        },
        preview: {
          lastDecision: getLastElement(decisions, '✅'),
          lastQuestion: getLastElement(questions, '❓'),
          lastRecommendation: getLastElement(recommendations, '🤖')
        },
        hasContent: decisionCount > 0 || questionCount > 0 || recommendationCount > 0,
        lastUpdate: stepNotes.updatedAt || new Date().toISOString(),
        sections: {
          decisions: decisions,
          questions: questions,
          recommendations: recommendations
        }
      });
    }
  }, [currentStepId, getStepNotes, answeredQuestions]);

  // CORRECTION CRITIQUE dans ChatGuided.js - Fonction updateNotesFromJsonSynthesis

  const updateNotesFromJsonSynthesis = useCallback(async (synthesisData) => {
    if (!currentStepId || !updateStepNoteSection) return false;

    try {
      console.log('📄 Mise à jour des notes à partir des données JSON');
      
      const timestamp = new Date().toLocaleString('fr-FR');
      const sourceTag = '[Notes]';
      let sectionsUpdated = 0;
      
      // CORRECTION PRINCIPALE : Récupérer le contenu existant et AJOUTER à la fin
      
      // Mise à jour section Décisions & Actions
      if (synthesisData.actions_accomplies && synthesisData.actions_accomplies.length > 0) {
        const existingDecisions = getStepNotes(currentStepId).decisions || '';
        const actionsText = synthesisData.actions_accomplies.map(action => `• ${action}`).join('\n');
        const newEntry = `✅ ${sourceTag} ${timestamp}\n${actionsText}\n\n`;
        
        // CORRECTION CRITIQUE : Concaténation au lieu de remplacement
        await updateStepNoteSection(currentStepId, 'decisions', existingDecisions + newEntry);
        sectionsUpdated++;
      }

      // Mise à jour section Questions & Défis  
      if (synthesisData.defis_identifies && synthesisData.defis_identifies.length > 0) {
        const existingQuestions = getStepNotes(currentStepId).questions || '';
        const defisText = synthesisData.defis_identifies.map(defi => `• ${defi}`).join('\n');
        const newEntry = `❓ ${sourceTag} ${timestamp}\n${defisText}\n\n`;
        
        // CORRECTION CRITIQUE : Concaténation au lieu de remplacement
        await updateStepNoteSection(currentStepId, 'questions', existingQuestions + newEntry);
        sectionsUpdated++;
      }

      // Mise à jour section Recommandations Aladinn
      if (synthesisData.recommandations && synthesisData.recommandations.length > 0) {
        const existingRecommendations = getStepNotes(currentStepId).ai_recommendations || '';
        const recommendationsText = synthesisData.recommandations.map(rec => `• ${rec}`).join('\n');
        const newEntry = `🔮 ${sourceTag} ${timestamp}\n${recommendationsText}\n\n`;
        
        // CORRECTION CRITIQUE : Concaténation au lieu de remplacement  
        await updateStepNoteSection(currentStepId, 'ai_recommendations', existingRecommendations + newEntry);
        sectionsUpdated++;
      }

      // Reste du code inchangé...
      if (sectionsUpdated > 0) {
        const notification = {
          id: Date.now(),
          type: 'synthesis_parsed',
          message: `Synthèse analysée: ${sectionsUpdated} sections mises à jour`,
          timestamp: new Date().toISOString()
        };
        
        setSyncNotifications(prev => [...prev.slice(-2), notification]);
        
        setTimeout(() => {
          setSyncNotifications(prev => prev.filter(n => n.id !== notification.id));
        }, 6000);
      }

      return sectionsUpdated > 0;
      
    } catch (error) {
      console.error('⛔ Erreur mise à jour notes depuis JSON:', error);
      return false;
    }
  }, [currentStepId, updateStepNoteSection, getStepNotes, setSyncNotifications]);

  // Fonction utilitaire pour générer le texte d'affichage à partir du JSON
  const generateDisplayTextFromJson = useCallback((synthesisData) => {
    let displayText = `Sur l'étape ${contextualData.currentStep?.titre || 'courante'}, ${synthesisData.synthese_generale || 'analyse en cours.'}\n\n`;
    
    if (synthesisData.actions_accomplies && synthesisData.actions_accomplies.length > 0) {
      displayText += "Actions accomplies :\n";
      synthesisData.actions_accomplies.forEach(action => {
        displayText += `• ${action}\n`;
      });
      displayText += "\n";
    }
    
    if (synthesisData.defis_identifies && synthesisData.defis_identifies.length > 0) {
      displayText += "Défis identifiés :\n";
      synthesisData.defis_identifies.forEach(defi => {
        displayText += `• ${defi}\n`;
      });
      displayText += "\n";
    }
    
    if (synthesisData.recommandations && synthesisData.recommandations.length > 0) {
      displayText += "Recommandations :\n";
      synthesisData.recommandations.forEach(rec => {
        displayText += `• ${rec}\n`;
      });
    }
    
    return displayText;
  }, [contextualData]);

  // Fonction de fallback pour parser du texte si le JSON échoue
  const parseTextualSynthesis = useCallback((textResponse) => {
    const extractBulletPoints = (text, sectionName) => {
      const regex = new RegExp(`${sectionName}\\s*:?\\s*([\\s\\S]*?)(?=(?:Actions|Défis|Recommandations)\\s*:|$)`, 'i');
      const match = text.match(regex);
      if (!match) return [];
      
      return match[1]
        .split('\n')
        .map(line => line.trim())
        .filter(line => line && line.includes('•'))
        .map(line => line.replace(/^•\s*/, ''));
    };
    
    return {
      synthese_generale: "Synthèse générée à partir du texte",
      actions_accomplies: extractBulletPoints(textResponse, 'Actions accomplies'),
      defis_identifies: extractBulletPoints(textResponse, 'Défis identifiés'),
      recommandations: extractBulletPoints(textResponse, 'Recommandations')
    };
  }, []);

  // Génération de questions avec contexte enrichi
  const generateThreeQuestions = useCallback(async () => {
    if (!canGenerateQuestions) return;

    setIsGeneratingQuestions(true);
    setErrorMessage('');
    
    try {
      const currentStep = contextualData.currentStep;
      const existingSynthesis = synthesisContent ? `\nSYNTHÈSE EXISTANTE:\n${synthesisContent}\n` : '';
      
      // Récupérer le contexte enrichi
      const stepNotes = getStepNotes(currentStepId) || {};
      const recentQuestions = answeredQuestions.slice(-3);
      
      // Construire les sections de contexte
      const notesContext = [
        stepNotes.decisions ? `DÉCISIONS PRISES:\n${stepNotes.decisions}` : '',
        stepNotes.questions ? `QUESTIONS/DÉFIS IDENTIFIÉS:\n${stepNotes.questions}` : '',
        stepNotes.ai_recommendations ? `RECOMMANDATIONS ALADINN:\n${stepNotes.ai_recommendations}` : ''
      ].filter(Boolean).join('\n\n');
      
      const questionHistory = recentQuestions.length > 0 
        ? recentQuestions.map(q => `Q: ${q.question}\nR: ${q.answer}`).join('\n\n')
        : 'Aucune question posée récemment.';
      
      const prompt = `Tu es Aladinn, assistant expert qui aide à progresser sur des projets concrets.

CONTEXTE:
- Objectif: ${userObjective || 'Non défini'}
- Étape courante: ${currentStep ? currentStep.titre : 'Aucune étape définie'}
- Ressources: ${savedSources?.length || 0} sources disponibles
${existingSynthesis}

NOTES ACTUELLES DE L'ÉTAPE:
${notesContext || 'Aucune note enregistrée pour cette étape.'}

QUESTIONS RÉCEMMENT POSÉES:
${questionHistory}

Génère 3 questions QCM pour évaluer l'avancement sur l'étape courante.
Chaque question doit avoir 3 options + "Autre".

RÈGLES IMPORTANTES:
1. NE PAS répéter les sujets déjà traités dans l'historique récent
2. S'appuyer sur les notes existantes pour approfondir ou compléter
3. Explorer de nouveaux aspects de l'étape qui n'ont pas encore été abordés
4. Proposer des questions concrètes et actionnables

Les questions doivent couvrir différents aspects comme:
- Actions réalisées et Décisions prises
- Défi, Difficultés, Obstacles nouveaux ou non résolus  
- Opportunité, idée, action, contact 

Format JSON uniquement:
[
  {
    "question": "question 1",
    "options": ["Option 1", "Option 2", "Option 3"]
  },
  {
    "question": "question 2",
    "options": ["Option 1", "Option 2", "Option 3"]
  },
  {
    "question": "question 3",
    "options": ["Option 1", "Option 2", "Option 3"]
  }
]`;

      const response = await askGPT(prompt);
      const questionsArray = extractQuestionsFromResponse(response);
      
      if (!questionsArray || questionsArray.length === 0) {
        throw new Error('Aucune question générée');
      }
      
      // Transformer en format utilisable
      const enhancedQuestions = questionsArray.map((q, index) => {
        const options = q.options.map((text, optIndex) => ({
          id: `opt${index}_${optIndex}`,
          text: text.trim(),
          value: `option${optIndex + 1}`
        }));
        
        options.push({
          id: `opt${index}_custom`,
          text: "Autre (préciser)",
          value: "custom"
        });
        
        return {
          id: `question_${Date.now()}_${index}`,
          index: index + 1,
          question: q.question,
          options,
          timestamp: new Date().toISOString()
        };
      });
      
      setCurrentQuestion(enhancedQuestions[0]);
      setQuestionsQueue(enhancedQuestions.slice(1));
      setCurrentSessionQuestions(0);
      
      console.log('Questions contextualisées générées avec succès');
      
    } catch (error) {
      console.error('Erreur génération questions:', error);
      setErrorMessage('Impossible de générer des questions. Réessayez.');
    } finally {
      setIsGeneratingQuestions(false);
    }
  }, [canGenerateQuestions, contextualData, userObjective, savedSources, synthesisContent, getStepNotes, currentStepId, answeredQuestions]);

  // Traitement des réponses avec logique améliorée
  const handleQuestionResponse = useCallback(async (questionData, selectedValues, customText = null) => {
    if (!selectedValues || selectedValues.length === 0) {
      setErrorMessage('Veuillez sélectionner au moins une option');
      return false;
    }

    // Construire le texte de réponse
    const responseTexts = selectedValues.map(value => {
      if (value === 'custom' && customText) return customText.trim();
      const option = questionData.options.find(opt => opt.value === value);
      return option ? option.text : '';
    }).filter(Boolean);
    
    const responseText = responseTexts.join(' • ');
    
    // Calculer les points
    let points = selectedValues.length === 1 ? POINTS_CONFIG.singleChoice : POINTS_CONFIG.multipleChoice;
    if (selectedValues.includes('custom')) points = POINTS_CONFIG.customResponse;
    
    // Sauvegarder l'historique pour la synthèse
    const answeredQuestion = {
      ...questionData,
      answer: responseText,
      selectedOptions: selectedValues,
      pointsEarned: points,
      timestamp: new Date().toISOString()
    };
    
    setAnsweredQuestions(prev => [...prev, answeredQuestion]);
    setUserProgress(prev => ({
      ...prev,
      totalPoints: prev.totalPoints + points,
      completedActions: prev.completedActions + 1
    }));

    setCurrentSessionQuestions(prev => prev + 1);

    setFeedbackMessage(`Réponse enregistrée +${points} points`);
    setTimeout(() => setFeedbackMessage(''), 3000);

    // Réinitialiser les sélections
    setCurrentQuestion(null);
    setSelectedOptions([]);
    setShowCustomInput(false);
    setCustomResponse('');
    
    const newSessionCount = currentSessionQuestions + 1;
    
    if (questionsQueue.length === 0) {
      if (newSessionCount >= QUESTIONS_BEFORE_HELP) {
        setTimeout(() => setShowAladinnHelp(true), 1000);
      } else {
        setTimeout(() => generateThreeQuestions(), 1000);
      }
    } else {
      if (newSessionCount >= QUESTIONS_BEFORE_HELP) {
        setTimeout(() => setShowAladinnHelp(true), 1000);
      } else {
        setTimeout(() => {
          setCurrentQuestion(questionsQueue[0]);
          setQuestionsQueue(prev => prev.slice(1));
        }, 1000);
      }
    }
    
    return true;
  }, [currentSessionQuestions, questionsQueue.length, generateThreeQuestions]);

  // Soumission des réponses multiples
  const submitMultipleResponse = useCallback(() => {
    if (!currentQuestion || selectedOptions.length === 0) {
      setErrorMessage('Veuillez sélectionner au moins une option');
      return;
    }

    if (selectedOptions.includes('custom') && !customResponse.trim()) {
      setErrorMessage('Veuillez préciser votre réponse personnalisée');
      return;
    }

    handleQuestionResponse(currentQuestion, selectedOptions, customResponse.trim() || null);
  }, [currentQuestion, selectedOptions, customResponse, handleQuestionResponse]);

  // Génération de synthèse avec approche JSON
// CORRECTION dans ChatGuided.js - Fonction requestAladinnHelp

const requestAladinnHelp = useCallback(async () => {
  setShowAladinnHelp(false);

  // CORRECTION : Utiliser TOUTES les réponses au lieu de se limiter aux 6 dernières
  const allAnswers = answeredQuestions; // Utiliser toutes les questions répondues
  const qaList = allAnswers.map(q => `Q: ${q.question}\nR: ${q.answer}`).join('\n\n');
  const currentStep = contextualData.currentStep;

  const prompt = `Tu es Aladinn, assistant expert.

CONTEXTE:
- Objectif: ${userObjective || 'Non défini'}
- Étape courante: ${currentStep ? currentStep.titre : 'Non définie'}
- Ressources: ${savedSources?.length || 0} sources

RÉPONSES À TOUTES LES QUESTIONS (${allAnswers.length} questions) :
${qaList}

Génère une synthèse au format JSON avec cette structure exacte :

{
  "synthese_generale": "Description générale de la situation en 1-2 phrases basée sur TOUTES les réponses",
  "actions_accomplies": [
    "Action 1 réalisée",
    "Action 2 réalisée"
  ],
  "defis_identifies": [
    "Défi/obstacle 1",
    "Défi/obstacle 2", 
    "Défi/obstacle 3"
  ],
  "recommandations": [
    "Recommandation prioritaire 1",
    "Recommandation prioritaire 2",
    "Recommandation prioritaire 3"
  ]
}

IMPORTANT: 
- Réponds UNIQUEMENT avec le JSON, sans texte avant ou après
- Analyse TOUTES les ${allAnswers.length} réponses fournies
- Si une section est vide, utilise un tableau vide []
- Chaque élément du tableau doit être une phrase complète et actionnable
- Synthétise l'ensemble des informations, pas seulement les plus récentes`;

  try {
    const response = await askGPT(prompt);
    
    // Parser le JSON directement
    let synthesisData;
    try {
      synthesisData = JSON.parse(response);
    } catch (jsonError) {
      console.warn('Échec parsing JSON, tentative de fallback:', jsonError);
      synthesisData = parseTextualSynthesis(response);
    }
    
    // Valider la structure
    if (!synthesisData.actions_accomplies || !synthesisData.defis_identifies || !synthesisData.recommandations) {
      throw new Error('Structure JSON invalide');
    }
    
    // Mise à jour directe des sections avec les données JSON
    const success = await updateNotesFromJsonSynthesis(synthesisData);
    
    // Générer le texte de synthèse pour l'affichage dans le chat
    const displayText = generateDisplayTextFromJson(synthesisData);
    
    // Sauvegarder la synthèse complète
    if (saveStepSynthesis && currentStepId) {
      await saveStepSynthesis(currentStepId, {
        content: displayText,
        jsonData: synthesisData,
        type: 'guided_session',
        timestamp: new Date().toISOString(),
        questionCount: answeredQuestions.length // Utiliser le nombre total de questions
      });
      console.log('💾 Synthèse sauvegardée dans le système');
    }
    
    // Sauvegarder l'historique des questions
    if (updateStepNoteSection && currentStepId) {
      await updateStepNoteSection(currentStepId, 'guidedHistory', JSON.stringify(answeredQuestions));
    }
    
    setSynthesisContent(displayText);
    setUserProgress(prev => ({ ...prev, totalPoints: prev.totalPoints + POINTS_CONFIG.synthesis }));
    
    // Afficher dans le chat
    await chatCore.sendMessage(displayText, { isSynthesisOnly: true });
    
    const feedbackMsg = success 
      ? `Synthèse générée à partir de ${allAnswers.length} questions et notes mises à jour automatiquement +${POINTS_CONFIG.synthesis} points`
      : `Synthèse générée à partir de ${allAnswers.length} questions +${POINTS_CONFIG.synthesis} points`;
    
    setFeedbackMessage(feedbackMsg);
    setTimeout(() => setFeedbackMessage(''), 5000);
    
  } catch (error) {
    console.error('Erreur génération synthèse:', error);
    setErrorMessage('Impossible de générer la synthèse. Réessayez.');
  }
}, [answeredQuestions, contextualData, userObjective, savedSources, saveStepSynthesis, currentStepId, updateStepNoteSection, chatCore, updateNotesFromJsonSynthesis, generateDisplayTextFromJson, parseTextualSynthesis]);

  const continueWithoutHelp = useCallback(() => {
    setShowAladinnHelp(false);
    setCurrentSessionQuestions(0);
    generateThreeQuestions();
  }, [generateThreeQuestions]);

  // Gestion des options
  const handleOptionToggle = useCallback((optionValue) => {
    if (optionValue === 'custom') {
      if (selectedOptions.includes('custom')) {
        setSelectedOptions(prev => prev.filter(opt => opt !== 'custom'));
        setShowCustomInput(false);
        setCustomResponse('');
      } else {
        setSelectedOptions(prev => [...prev, 'custom']);
        setShowCustomInput(true);
      }
    } else {
      setSelectedOptions(prev => {
        if (prev.includes(optionValue)) {
          return prev.filter(opt => opt !== optionValue);
        } else {
          return [...prev, optionValue];
        }
      });
    }
  }, [selectedOptions]);

  // Envoi message libre
  const sendFreeConversationMessage = useCallback(async (questionText) => {
    if (!questionText.trim()) return;

    const currentStep = contextualData.currentStep;
    const contextualizedMessage = currentStep 
      ? `[Contexte: Étape "${currentStep.titre}"] ${questionText}`
      : questionText;

    try {
      await chatCore.sendMessage(contextualizedMessage, {
        isContextualQuestion: true,
        stepContext: currentStep,
        stepId: currentStepId,
        userObjective: userObjective
      });
      
      setFreeConversationInput('');
    } catch (error) {
      console.error('Erreur envoi message libre:', error);
      setErrorMessage('Impossible d\'envoyer le message.');
    }
  }, [contextualData, chatCore, currentStepId, userObjective]);

  // Changement de mode
  const changeMode = useCallback((newMode) => {
    setChatMode(newMode);
    setCurrentQuestion(null);
    setQuestionsQueue([]);
    setErrorMessage('');
    setShowAladinnHelp(false);
    setShowCustomInput(false);
    setCustomResponse('');
    setSelectedOptions([]);
    setFreeConversationInput('');
    setCurrentSessionQuestions(0);
    
    if (newMode === 'guided') {
      setTimeout(() => generateThreeQuestions(), 500);
    }
  }, [generateThreeQuestions]);

  // Reset sélections lors du changement de question
  useEffect(() => {
    if (currentQuestion) {
      setSelectedOptions([]);
      setShowCustomInput(false);
      setCustomResponse('');
    }
  }, [currentQuestion?.id]);

  // Calcul du résumé d'avancement
  const advancementSummary = useMemo(() => {
    if (answeredQuestions.length === 0) return null;
    
    const totalPoints = answeredQuestions.reduce((sum, q) => sum + (q.pointsEarned || 0), 0);
    
    return {
      totalAnswered: answeredQuestions.length,
      totalPoints,
      decisions: Math.ceil(answeredQuestions.length / 3),
      challenges: Math.floor(answeredQuestions.length / 3),
      hasSynthesis: !!synthesisContent,
      lastActivity: answeredQuestions[answeredQuestions.length - 1]?.timestamp,
      currentSessionCount: currentSessionQuestions
    };
  }, [answeredQuestions, synthesisContent, currentSessionQuestions]);

  return {
    // États principaux
    chatMode,
    isGuidedMode,
    userProgress,
    currentQuestion,
    questionsQueue,
    isGeneratingQuestions,
    answeredQuestions,
    feedbackMessage,
    errorMessage,
    contextualData,
    showAladinnHelp,
    advancementSummary,
    customResponse,
    showCustomInput,
    selectedOptions,
    freeConversationInput,
    synthesisContent,
    syncNotifications,
    synthesisData,
    currentSessionQuestions,

    // Actions
    changeMode,
    generateThreeQuestions,
    handleQuestionResponse,
    handleOptionToggle,
    submitMultipleResponse,
    requestAladinnHelp,
    continueWithoutHelp,
    setCustomResponse,
    setFreeConversationInput,
    sendFreeConversationMessage,
    
    // Utilitaires
    canGenerateQuestions,
    setFeedbackMessage,
    setErrorMessage,
    setSyncNotifications
  };
};

// Composants d'interface - Composant pour les questions QCM séquentielles
export const SequentialQCMQuestion = ({ 
  questionData, 
  onOptionToggle,
  selectedOptions,
  showCustomInput,
  customResponse,
  setCustomResponse,
  submitMultipleResponse
}) => {
  if (!questionData) return null;

  const hasSelection = selectedOptions.length > 0;
  const needsCustomText = selectedOptions.includes('custom') && !customResponse.trim();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      {/* En-tête avec design compact */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-white font-bold text-sm">{questionData.index}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-lg font-medium text-gray-900 leading-snug mb-1">
              Question {questionData.index} : {questionData.question}
            </h3>
            <div className="text-xs text-gray-500">
              Plusieurs choix possibles • {selectedOptions.length} sélectionné{selectedOptions.length > 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      {/* Container scrollable pour les options */}
      <div className="max-h-80 sm:max-h-96 overflow-y-auto">
        {/* Options */}
        <div className="p-4 space-y-2">
          {questionData.options.map((option) => {
            const isSelected = selectedOptions.includes(option.value);
            const isCustomOption = option.value === 'custom';
            
            return (
              <button
                key={option.id}
                onClick={() => onOptionToggle(option.value)}
                className={`w-full p-3 text-left rounded-lg border-2 transition-all duration-200 flex items-center gap-3 text-sm sm:text-base ${
                  isSelected
                    ? isCustomOption
                      ? 'border-purple-300 bg-purple-50'
                      : 'border-green-300 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 active:bg-gray-100'
                }`}
              >
                <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isSelected
                    ? isCustomOption
                      ? 'border-purple-500 bg-purple-500'
                      : 'border-green-500 bg-green-500'
                    : 'border-gray-400'
                }`}>
                  {isSelected && <Check size={10} className="text-white sm:w-3 sm:h-3" />}
                </div>
                <span className="text-gray-800 leading-relaxed">{option.text}</span>
              </button>
            );
          })}
        </div>

        {/* Input personnalisé */}
        {showCustomInput && (
          <div className="px-4 pb-4">
            <div className="bg-purple-50 rounded-lg border border-purple-200 p-3">
              <label className="block text-sm font-medium text-purple-800 mb-2">
                Précisez votre réponse :
              </label>
              <textarea
                value={customResponse}
                onChange={(e) => setCustomResponse(e.target.value)}
                placeholder="Décrivez votre situation..."
                className="w-full p-3 border border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none text-sm"
                rows={3}
              />
            </div>
          </div>
        )}
      </div>

      {/* Bouton de validation fixé en bas - Toujours visible */}
      <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4">
        <button
          onClick={submitMultipleResponse}
          disabled={!hasSelection || needsCustomText}
          className={`w-full p-3 sm:p-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 text-sm sm:text-base ${
            hasSelection && !needsCustomText
              ? 'bg-green-500 text-white hover:bg-green-600 shadow-sm active:bg-green-700'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
        >
          <Send size={16} />
          {hasSelection 
            ? selectedOptions.length > 1 
              ? `Valider mes ${selectedOptions.length} choix`
              : 'Valider ma réponse'
            : 'Choisir au moins une option'
          }
        </button>
        
        {/* Indicateur de sélection sur mobile */}
        {hasSelection && (
          <div className="mt-2 text-center">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <Check size={12} className="mr-1" />
              {selectedOptions.length} option{selectedOptions.length > 1 ? 's' : ''} sélectionnée{selectedOptions.length > 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant pour l'offre d'aide d'Aladinn
export const AladinnHelpOffer = ({ 
  answeredQuestions,
  currentSessionQuestions,
  onRequestHelp,
  onContinue
}) => {
  const totalQuestions = answeredQuestions.length;
  const sessionQuestions = currentSessionQuestions || 0;
  
  return (
    <div className="mb-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-3 sm:p-4 border border-blue-200">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
          <MessageSquare size={16} className="text-white sm:w-5 sm:h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="font-medium text-blue-800 text-sm sm:text-base">Aladinn</span>
            <div className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              Aide disponible
            </div>
          </div>
          
          <div className="text-blue-800 mb-3 text-sm sm:text-base leading-relaxed">
            Tu as répondu à {sessionQuestions >= 3 ? '3' : sessionQuestions} questions dans cette série 
            {totalQuestions > sessionQuestions && ` (${totalQuestions} au total)`}. 
            Veux-tu que je t'aide à analyser tes choix ?
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={onRequestHelp}
              className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all flex items-center justify-center gap-2 text-sm font-medium"
            >
              <MessageSquare size={14} />
              Oui, aide-moi
            </button>
            <button
              onClick={onContinue}
              className="flex-1 px-3 py-2 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 transition-all text-sm font-medium"
            >
              Continuer les questions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant pour le sélecteur de mode de chat (ANCIEN - conservé pour compatibilité)
export const ChatModeSelector = ({ 
  currentMode, 
  onModeChange, 
  userProgress = {},
  contextualData
}) => {
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 mb-4 border border-blue-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-blue-600" />
          Mode d'accompagnement
        </h3>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => onModeChange('conversation')}
          className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
            currentMode === 'conversation'
              ? 'border-blue-500 bg-blue-50 shadow-md'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-gray-800">Conversation libre</span>
          </div>
          <p className="text-sm text-gray-600">
            Discussion ouverte avec questions suggérées
          </p>
        </button>
        
        <button
          onClick={() => onModeChange('guided')}
          className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
            currentMode === 'guided'
              ? 'border-green-500 bg-green-50 shadow-md'
              : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <HelpCircle className="h-5 w-5 text-green-600" />
            <span className="font-medium text-gray-800">Progression guidée</span>
            <Star className="h-4 w-4 text-yellow-500" />
          </div>
          <p className="text-sm text-gray-600">
            Questions structurées avec sauvegarde automatique
          </p>
        </button>
      </div>
    </div>
  );
};

// NOUVEAU : Composant Toggle compact pour basculer entre les modes
export const GuidedModeToggle = ({ 
  currentMode, 
  onModeChange,
  disabled = false
}) => {
  const isGuidedMode = currentMode === 'guided';
  
  return (
    <div className="bg-white border-t border-gray-200 p-2 md:p-3">
      <div className="flex items-center justify-center gap-2">
        {/* Label Conversation */}
        <button
          onClick={() => !disabled && onModeChange('conversation')}
          disabled={disabled}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all duration-200 ${
            !isGuidedMode
              ? 'text-blue-600 font-medium'
              : 'text-gray-500 hover:text-gray-700'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <MessageCircle size={14} className="md:w-4 md:h-4" />
          <span className="text-xs md:text-sm">Conversation</span>
        </button>

        {/* Toggle Switch */}
        <button
          onClick={() => !disabled && onModeChange(isGuidedMode ? 'conversation' : 'guided')}
          disabled={disabled}
          className={`relative inline-flex h-6 w-12 md:h-7 md:w-14 items-center rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            isGuidedMode
              ? 'bg-green-500 focus:ring-green-500'
              : 'bg-blue-500 focus:ring-blue-500'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          title={isGuidedMode ? 'Passer en mode Conversation' : 'Passer en mode Avancement'}
        >
          <span
            className={`inline-block h-4 w-4 md:h-5 md:w-5 transform rounded-full bg-white shadow-lg transition-transform duration-300 ${
              isGuidedMode ? 'translate-x-6 md:translate-x-7' : 'translate-x-1'
            }`}
          />
        </button>

        {/* Label Avancement */}
        <button
          onClick={() => !disabled && onModeChange('guided')}
          disabled={disabled}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all duration-200 ${
            isGuidedMode
              ? 'text-green-600 font-medium'
              : 'text-gray-500 hover:text-gray-700'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <BarChart3 size={14} className="md:w-4 md:h-4" />
          <span className="text-xs md:text-sm">Avancement</span>
        </button>
      </div>

      {/* Indicateur de mode actif sur mobile */}
      <div className="mt-1 text-center md:hidden">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
          isGuidedMode
            ? 'bg-green-100 text-green-700'
            : 'bg-blue-100 text-blue-700'
        }`}>
          {isGuidedMode ? '📊 Avancement' : '💬 Conversation'}
        </span>
      </div>
    </div>
  );
};

// Composant principal pour les questions contextuelles
export const ContextualQuestionsPanel = ({ 
  userObjective,
  currentStep,
  savedSources,
  actionPlan,
  chatCore,
  guidedState,
  onNavigateToNotes
}) => {
  const {
    chatMode,
    currentQuestion,
    isGeneratingQuestions,
    handleOptionToggle,
    submitMultipleResponse,
    selectedOptions,
    customResponse,
    setCustomResponse,
    showCustomInput,
    generateThreeQuestions,
    showAladinnHelp,
    requestAladinnHelp,
    continueWithoutHelp,
    errorMessage,
    setErrorMessage,
    syncNotifications,
    currentSessionQuestions
  } = guidedState;

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Messages d'erreur */}
      {errorMessage && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 flex items-center gap-3">
          <AlertCircle size={20} className="text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-sm">{errorMessage}</p>
          </div>
          <button 
            onClick={() => setErrorMessage('')}
            className="p-1 hover:bg-red-100 rounded-full text-red-500"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Mode questions guidées */}
      {chatMode === 'guided' && (
        <div className="flex-1 flex flex-col min-h-0">
          {/* Aide d'Aladinn */}
          {showAladinnHelp && (
            <AladinnHelpOffer
              answeredQuestions={guidedState.answeredQuestions}
              currentSessionQuestions={currentSessionQuestions}
              onRequestHelp={requestAladinnHelp}
              onContinue={continueWithoutHelp}
            />
          )}

          {/* Question séquentielle - Prend l'espace disponible */}
          {currentQuestion && (
            <div className="flex-1 min-h-0">
              <SequentialQCMQuestion
                questionData={currentQuestion}
                onOptionToggle={handleOptionToggle}
                selectedOptions={selectedOptions}
                showCustomInput={showCustomInput}
                customResponse={customResponse}
                setCustomResponse={setCustomResponse}
                submitMultipleResponse={submitMultipleResponse}
              />
            </div>
          )}

          {/* État de génération */}
          {isGeneratingQuestions && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-center gap-3">
                <Loader className="h-6 w-6 animate-spin text-blue-600" />
                <div>
                  <p className="text-blue-800 font-medium text-sm sm:text-base">
                    Aladinn prépare des questions contextualisées...
                  </p>
                  <p className="text-blue-700 text-xs sm:text-sm">
                    Questions adaptées à votre étape actuelle
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Boutons d'action */}
          {!currentQuestion && !isGeneratingQuestions && !showAladinnHelp && !errorMessage && (
            <button
              onClick={generateThreeQuestions}
              className="w-full p-4 border-2 border-dashed border-green-300 text-green-700 rounded-lg hover:border-green-400 hover:bg-green-50 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <HelpCircle size={20} />
              Générer de nouvelles questions guidées
            </button>
          )}

          {!currentQuestion && !isGeneratingQuestions && !showAladinnHelp && errorMessage && (
            <button
              onClick={() => {
                setErrorMessage('');
                generateThreeQuestions();
              }}
              className="w-full p-4 border-2 border-dashed border-red-300 text-red-700 rounded-lg hover:border-red-400 hover:bg-red-50 transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              <RefreshCw size={20} />
              Réessayer de générer des questions
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Composant pour les messages de feedback
export const FeedbackMessage = ({ 
  message, 
  onClose, 
  type = 'success',
  autoHide = true,
  duration = 4000
}) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (autoHide) {
      const timer = setTimeout(() => {
        setVisible(false);
        if (onClose) onClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [autoHide, duration, onClose]);

  if (!visible || !message) return null;

  const bgColor = type === 'success' ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200';
  const textColor = type === 'success' ? 'text-green-700' : 'text-orange-700';
  const icon = type === 'success' ? <CheckCircle className="w-5 h-5" /> : <Trophy className="w-5 h-5" />;

  return (
    <div className={`mb-4 ${bgColor} border ${textColor} p-3 rounded-lg animate-bounce`}>
      <div className="flex items-center">
        {icon}
        <p className="font-medium ml-2 flex-1">{message}</p>
      </div>
    </div>
  );
};
